import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useService } from '../../../components/context/ServiceContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getPrimaryImage, getImagesArray } from '../../../utils/imageUtils';
import CatalogImageModal from './CatalogImageModal';
import AddToRequestButton from '../../../components/request-builder/AddToRequestButton';
import { formatDuration } from '../../../utils/durationUtils';
import { formatItemPrice } from '../../../utils/pricingUtils';


const CatalogCard = ({ service, onAddToCart }) => {
  const [imageError, setImageError] = useState(false);
  const [actionActive, setActionActive] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = getImagesArray(service?.imageUrl);
  const hasMultiple = images.length > 1;
  const currentImage = images[currentIdx]?.url ?? getPrimaryImage(service?.imageUrl);
  const { addService } = useService();
  const navigate = useNavigate();
  const { companyPath } = useParams();
  const isProduct = service?.type === 'product';
  const actionLabel = isProduct ? 'Add to Cart' : 'Book';
  const activeLabel = isProduct ? 'Added' : 'Booking';

  const handleAction = (e) => {
    e?.stopPropagation();
    setActionActive(true);
    if (isProduct) {
      onAddToCart?.(service);
    } else {
      addService(service);
      navigate(`/${companyPath}/date-selection`);
    }
    setTimeout(() => setActionActive(false), 2000);
  };

  const stockLabel = isProduct && service?.currentStock != null
    ? service.isOutOfStock ? 'Out of Stock'
      : service.isLowStock ? `${service.currentStock} left`
      : 'In Stock'
    : null;

  const stockColor = service?.isOutOfStock ? 'text-red-400'
    : service?.isLowStock ? 'text-yellow-400'
    : 'text-emerald-400';

  return (
    <div className="bg-card border border-border rounded-xl transition-all duration-250 ease-elegant hover:shadow-lg overflow-hidden">

      {/* ── Mobile: collapsed image-first view ── */}
      <div
        className={`sm:hidden ${expanded ? 'hidden' : 'block'} relative aspect-[4/3] cursor-pointer`}
        onClick={() => setExpanded(true)}
      >
        {!imageError ? (
          <Image
            src={currentImage}
            alt={service?.name}
            className="w-full h-full object-cover transition-opacity duration-200"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <Icon name="ImageOff" size={40} color="var(--color-muted-foreground)" />
          </div>
        )}

        {/* Prev / Next buttons */}
        {hasMultiple && (
          <>
            <button
              onClick={e => { e.stopPropagation(); setCurrentIdx(i => (i - 1 + images.length) % images.length); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            >
              <Icon name="ChevronLeft" size={16} color="white" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); setCurrentIdx(i => (i + 1) % images.length); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            >
              <Icon name="ChevronRight" size={16} color="white" />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {hasMultiple && (
          <div className="absolute bottom-14 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-none">
            {images.map((_, i) => (
              <span
                key={i}
                className={`block rounded-full transition-all duration-200 ${
                  i === currentIdx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Popular badge — top left */}
        {service?.isPopular && (
          <div className="absolute top-2.5 left-2.5 z-10">
            <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full shadow-md">
              <Icon name="Star" size={11} />
              Popular
            </span>
          </div>
        )}

        {/* Add / Book button — top right */}
        <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <AddToRequestButton
            item={service}
            size="xs"
            className="border-white/70 bg-background/90 shadow-lg backdrop-blur-sm"
          />
          <button
            onClick={handleAction}
            disabled={isProduct && service?.isOutOfStock}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg transition-all duration-200 ${
              actionActive
                ? 'bg-emerald-500 text-white'
                : 'bg-white/90 backdrop-blur-sm text-gray-900 hover:bg-white'
            } disabled:opacity-50`}
          >
            <Icon name={actionActive ? 'Check' : isProduct ? 'ShoppingCart' : 'Calendar'} size={12} />
            {actionActive ? activeLabel : actionLabel}
          </button>
        </div>

        {/* Bottom frosted overlay */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-md px-3 pt-6 pb-3 pointer-events-none antialiased rounded-b-xl"
          style={{
            WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 40%)',
            maskImage: 'linear-gradient(to bottom, transparent, black 40%)',
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-white font-semibold text-lg leading-tight truncate font-caption">{service?.name}</p>
              <p className="text-white/75 text-sm font-caption mt-0.5">
                {formatItemPrice(service)}
                {service?.durationInMinutes != null && (
                  <span className="ml-2 text-white/50">· {formatDuration(service.durationInMinutes)}</span>
                )}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {stockLabel && (
                <span className={`text-sm font-semibold font-caption ${stockColor}`} style={{ textShadow: 'none' }}>{stockLabel}</span>
              )}
              <span className="flex items-center gap-0.5 text-white/50 text-xs font-caption">
                Details
                <Icon name="ChevronDown" size={10} color="rgba(255,255,255,0.5)" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: expanded + Desktop: always visible ── */}
      <div className={`p-4 md:p-5 lg:p-6 ${expanded ? 'block' : 'hidden'} sm:block`}>
        <div className="flex flex-col sm:flex-row gap-4 md:gap-5 lg:gap-6">

          {/* Image — desktop click opens lightbox */}
          <div className="flex-shrink-0">
            <div
              className="relative w-full sm:w-20 md:w-24 lg:w-28 h-24 sm:h-20 md:h-24 lg:h-28 rounded-lg overflow-hidden bg-muted sm:cursor-pointer group"
              onClick={() => setLightboxOpen(true)}
            >
              {!imageError ? (
                <Image
                  src={getPrimaryImage(service?.imageUrl)}
                  alt={service?.name}
                  className="w-full h-full object-cover sm:group-hover:scale-105 transition-transform duration-300"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="ImageOff" size={32} color="var(--color-muted-foreground)" />
                </div>
              )}
              {/* Zoom hint on hover */}
              {!imageError && (
                <div className="absolute inset-0 bg-black/0 sm:group-hover:bg-black/20 transition-colors duration-200 hidden sm:flex items-center justify-center">
                  <Icon name="ZoomIn" size={18} color="white" className="opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200" />
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-base md:text-lg lg:text-xl text-foreground truncate">
                  {service?.name}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground font-caption mt-1">
                  {service?.category}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Collapse back — mobile only */}
                <button
                  onClick={() => { setExpanded(false); setCurrentIdx(0); }}
                  className="sm:hidden p-1.5 rounded-full hover:bg-muted transition-colors"
                  aria-label="Collapse"
                >
                  <Icon name="ChevronUp" size={16} color="var(--color-muted-foreground)" />
                </button>

                <AddToRequestButton item={service} size="xs" />
                <Button
                  onClick={handleAction}
                  variant={actionActive ? 'success' : 'outline'}
                  size="sm"
                  iconName={actionActive ? 'Check' : isProduct ? 'ShoppingCart' : 'Calendar'}
                  disabled={isProduct && service?.isOutOfStock}
                >
                  {actionActive ? activeLabel : actionLabel}
                </Button>
              </div>
            </div>

            <p className="text-sm md:text-base text-foreground/80 font-caption line-clamp-2 mb-3">
              {service?.description}
            </p>

            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2">
                <Icon name="ReceiptCent" size={16} color="var(--color-primary)" />
                <span className="font-caption font-semibold text-sm md:text-base text-foreground whitespace-nowrap">
                  {formatItemPrice(service)}
                </span>
              </div>

              {service?.durationInMinutes != null && (
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} color="var(--color-secondary)" />
                  <span className="font-caption text-sm md:text-base text-muted-foreground whitespace-nowrap">
                    {formatDuration(service?.durationInMinutes)}
                  </span>
                </div>
              )}

              {service?.isPopular && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-caption rounded-full flex items-center gap-1">
                  <Icon name="Star" size={12} />
                  Popular
                </span>
              )}

              {isProduct && service?.currentStock != null && (
                <div className="flex items-center gap-2">
                  <Icon
                    name="Package"
                    size={16}
                    color={service.isOutOfStock ? 'var(--color-destructive)' : service.isLowStock ? 'var(--color-warning)' : 'var(--color-success)'}
                  />
                  <span className={`font-caption text-xs whitespace-nowrap ${
                    service.isOutOfStock ? 'text-destructive' :
                    service.isLowStock ? 'text-warning' :
                    'text-success'
                  }`}>
                    {service.isOutOfStock ? 'Out of Stock' :
                     service.isLowStock ? `Low Stock (${service.currentStock})` :
                     `In Stock (${service.currentStock})`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CatalogImageModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        service={service}
        images={images}
        initialIndex={0}
        onAction={handleAction}
        actionActive={actionActive}
        isProduct={isProduct}
      />
    </div>
  );
};

export default CatalogCard;
